import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient } from "@tanstack/react-query";
import { ChevronsRight } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import GoBackButton from "@/common/components/GoBackButton";
import { createSchema, PermissionValidationSchema } from "@/features/permissions/schemas/create.schema";
import useCreateClientPermission from "../../hooks/useCreateClientPermission";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import useDebounce from "@/common/hooks/useDebounce";
import { useClientPermissionsOperationKlasses } from "@/common/hooks/queries/useClientPermissionsOperationKlasses";
import { useGoBack } from "@/common/hooks/useGoBack";

const ClientPermissionsCreate = () => {
  const goBack = useGoBack();
  const queryClient = new QueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchPermissionOperationKlass, setSearchPermissionOperationKlass] = useDebounce('');

  const {
    mutate: createClientPermission,
    isPending: isCreateClientPermissionPending,
  } = useCreateClientPermission({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientPermissions'] })
      goBack({ fallback: '/client-permissions', searchLastRouteFromFallback: true })
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const { data: operationKlassesOptions } = useClientPermissionsOperationKlasses({
    filters: { search: searchPermissionOperationKlass },
    mapper: (data: string[]) => data?.map((item) => ({ value: item, label: item }))
  });

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<PermissionValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const onSubmit: SubmitHandler<PermissionValidationSchema> = (data) => createClientPermission(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/client-permissions" />

          <p className="text-white-400 font-normal">
            Permissões - Clientes
          </p>
          <ChevronsRight color='#ffffffb3' />

          <h2>
            Nova permissão
          </h2>
        </div>

        <div className="flex gap-4">
          <Button
            variant='outline-error'
            onClick={() => goBack({ fallback: '/client-permissions', searchLastRouteFromFallback: true })}
            disabled={isCreateClientPermissionPending}
          >
            Cancelar
          </Button>

          <Button data-testid='create_permission' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" isLoading={isCreateClientPermissionPending}>
            Confirmar
          </Button>
        </div>
      </div>

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
        <p className="text-lg font-bold">
          Nova permissão
        </p>

        <div className="flex gap-2">
          <TextField
            label="Nome"
            markAsRequired
            errorMessage={errors?.display_name?.message}
            className="w-1/2"
            {...register('display_name')}
            disabled={isCreateClientPermissionPending}
          />
          <SelectController
            name='operation_klass'
            control={control}
            label="Classe de operação"
            markAsRequired
            onInputChange={setSearchPermissionOperationKlass}
            options={operationKlassesOptions}
            errorMessage={errors?.operation_klass?.message}
            className="w-1/2"
            isDisabled={isCreateClientPermissionPending}
          />
        </div>

        <TextField
          label="Descrição"
          markAsRequired
          errorMessage={errors?.description?.message}
          {...register('description')}
          disabled={isCreateClientPermissionPending}
        />

        <TextArea
          label="Propiedades"
          placeholder="{}"
          {...register('properties')}
          errorMessage={errors.properties?.message || undefined}
          rows={8}
          disabled={isCreateClientPermissionPending}
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default ClientPermissionsCreate;
